/** PLT-6970 — how a supplier's spend is typed and shown on the Suppliers page. */

const AMOUNT_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * An amount as the list shows it: whole currency units, comma-grouped, no currency sign — the
 * column header names the buyer's spend currency once. The grouping is the one the
 * company-details form already types revenue with.
 *
 * **Whole units, not cents.** The amount exists to weight suppliers against each other, where a
 * fraction of a unit changes nothing, and a ten-digit annual spend with decimals does not fit the
 * list's column.
 */
export function formatSpendAmount(amount: number): string {
  return AMOUNT_FORMAT.format(amount);
}

/**
 * What the reader has typed so far, cleaned up as they type: digits and one decimal point, grouped
 * with commas — the same rule the form's revenue field types by. Anything else (a currency sign, a
 * minus, a letter) is dropped rather than refused, so the field can never hold a value that fails to
 * parse.
 *
 * The decimal point is kept while typing even though the list shows whole units: dropping it would
 * turn "1234.5", typed one key at a time, into 12,345 — the next digit lands on the integer part.
 */
export function formatSpendInput(text: string): string {
  const cleaned = text.replace(/[^0-9.]/g, '');
  const [integerPart = '', ...decimalParts] = cleaned.split('.');
  const hasDecimalPoint = cleaned.includes('.');
  const decimals = decimalParts.join('').slice(0, 2);
  const digits = integerPart.replace(/^0+(?=\d)/, '');
  const groupedInteger = digits === '' ? (hasDecimalPoint ? '0' : '') : digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return hasDecimalPoint ? `${groupedInteger}.${decimals}` : groupedInteger;
}

/** The typed text back as a number; an empty field is `null`, which leaves the spend unentered. */
export function parseSpendInput(text: string): number | null {
  const raw = text.replace(/,/g, '').trim();
  if (raw === '' || raw === '.') {
    return null;
  }
  return Number(raw);
}
